import { useRef, useState } from "react";
import { Loader2, Wand2, Copy, Square } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

const FN_URL = `https://${import.meta.env.VITE_SUPABASE_PROJECT_ID}.supabase.co/functions/v1/lead-insight`;
const LANES = ["", "Coaching", "Keynote Speaking", "Consulting", "Advocacy (FSAP)", "Masterclass", "Strategy Session", "Books / Other"];
const field = "w-full border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-orange";

const AdminLeadInsight = () => {
  const [name, setName] = useState("");
  const [lane, setLane] = useState("");
  const [response, setResponse] = useState("");
  const [output, setOutput] = useState("");
  const [thinking, setThinking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const run = async () => {
    setError(null); setOutput(""); setBusy(true); setThinking(true);
    const ac = new AbortController(); abortRef.current = ac;
    try {
      const { data: s } = await supabase.auth.getSession();
      const res = await fetch(FN_URL, {
        method: "POST",
        signal: ac.signal,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${s.session?.access_token ?? ""}`,
          apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
        },
        body: JSON.stringify({ lead_name: name, lane, response }),
      });
      if (!res.ok || !res.body) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.error ?? `Request failed (${res.status})`);
      }
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const lines = buf.split("\n"); buf = lines.pop() ?? "";
        for (const line of lines) {
          if (!line.startsWith("data:")) continue;
          const raw = line.slice(5).trim();
          if (!raw || raw === "[DONE]") continue;
          try {
            const ev = JSON.parse(raw);
            if (ev.type === "response.output_text.delta") { setThinking(false); setOutput((o) => o + ev.delta); }
            else if (ev.type === "response.failed" || ev.type === "error")
              throw new Error(ev.response?.error?.message ?? ev.message ?? "AI request failed");
          } catch (e) { if (e instanceof Error && !(e instanceof SyntaxError)) throw e; }
        }
      }
    } catch (e) {
      if (!(e instanceof DOMException && e.name === "AbortError")) setError(e instanceof Error ? e.message : String(e));
    } finally { setBusy(false); setThinking(false); abortRef.current = null; }
  };

  return (
    <main className="container max-w-5xl py-28">
      <h1 className="font-display uppercase text-3xl">Lead Insight</h1>
      <p className="text-muted-foreground mt-2 text-sm">
        Paste a new lead's form response. AI summarizes their needs, recommends an offer, and drafts a tailored follow-up.
      </p>

      <div className="grid md:grid-cols-2 gap-8 mt-8">
        <div className="space-y-4">
          <input className={field} placeholder="Lead name (optional)" value={name} onChange={(e) => setName(e.target.value)} maxLength={200} />
          <select className={field} value={lane} onChange={(e) => setLane(e.target.value)} aria-label="Inquiry lane">
            {LANES.map((l) => <option key={l} value={l}>{l || "Inquiry lane (optional)"}</option>)}
          </select>
          <textarea className={field} rows={14} maxLength={12000} placeholder="Paste the lead's full form response…"
            value={response} onChange={(e) => setResponse(e.target.value)} />
          <div className="flex gap-3">
            <button type="button" onClick={run} disabled={busy || response.trim().length < 10}
              className="inline-flex items-center gap-2 bg-cta text-white font-display uppercase tracking-[0.2em] text-xs px-6 py-3 disabled:opacity-40">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />} Analyze lead
            </button>
            {busy && (
              <button type="button" onClick={() => abortRef.current?.abort()} className="inline-flex items-center gap-2 border border-border px-4 py-3 text-xs uppercase">
                <Square className="h-3 w-3" /> Stop
              </button>
            )}
          </div>
          {error && <p className="text-sm text-destructive" role="alert">{error}</p>}
        </div>

        <div className="border border-border p-6 min-h-[300px]">
          <div className="flex justify-between items-center mb-3">
            <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground">AI result</span>
            {output && !busy && (
              <button type="button" onClick={() => navigator.clipboard.writeText(output)} className="inline-flex items-center gap-1 text-xs hover:text-orange">
                <Copy className="h-3.5 w-3.5" /> Copy
              </button>
            )}
          </div>
          {thinking && <p className="text-sm text-muted-foreground flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Reading the response…</p>}
          {!output && !busy && <p className="text-sm text-muted-foreground">Results appear here.</p>}
          <div className="whitespace-pre-wrap text-sm leading-relaxed">{output.replace(/\*\*/g, "")}</div>
        </div>
      </div>
    </main>
  );
};

export default AdminLeadInsight;
